/**
 * Giorni di calendario, senza passare da UTC.
 *
 * Una data che è un giorno — la chiusura prevista di una trattativa, la
 * validità di un listino, la data di un ordine — non è un istante, e
 * trattarla come tale la sposta:
 *
 *  - `toISOString().slice(0, 10)` è il giorno di Greenwich: fra mezzanotte e
 *    le due, in Italia, è ancora ieri, e «Nuovo ordine» proponeva la data di
 *    ieri;
 *  - le date travasate dal v2 stanno a mezzanotte italiana, cioè alle 22 o
 *    alle 23 del giorno prima in UTC: prenderne i primi dieci caratteri dava
 *    il giorno prima, e la finestra «Modifica» lo salvava anche senza che
 *    nessuno l'avesse toccato.
 *
 * Qui il giorno si legge sempre in un fuso preciso: quello di chi usa il
 * programma per «oggi», quello italiano per le date che a database sono
 * istanti ma vogliono dire un giorno.
 */

export const FUSO_ITALIANO = 'Europe/Rome';

const due = (n: number) => String(n).padStart(2, '0');

/** Il giorno sul calendario di chi usa il programma: «2026-10-25» anche alle 00:30. */
export function giornoLocale(istante: Date = new Date()): string {
  return `${istante.getFullYear()}-${due(istante.getMonth() + 1)}-${due(istante.getDate())}`;
}

/**
 * Il giorno di una data salvata come istante, letto in Italia, dove è stata
 * scritta: «2027-02-27T23:00:00Z» è il 28 febbraio. Un giorno già semplice
 * passa com'è; un valore vuoto o non valido diventa una stringa vuota, che è
 * quello che vuole un campo data vuoto.
 */
export function giornoItaliano(valore: string | null | undefined): string {
  if (!valore) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(valore)) return valore;
  const d = new Date(valore);
  if (Number.isNaN(d.getTime())) return '';
  // en-CA scrive le date come AAAA-MM-GG, il formato dei campi data
  return new Intl.DateTimeFormat('en-CA', { timeZone: FUSO_ITALIANO }).format(d);
}

/** «2026-10-25» → il Date della mezzanotte locale di quel giorno, per scriverlo nella lingua di chi legge. */
export function daGiorno(giorno: string): Date | null {
  const parti = /^(\d{4})-(\d{2})-(\d{2})$/.exec(giorno);
  return parti ? new Date(Number(parti[1]), Number(parti[2]) - 1, Number(parti[3])) : null;
}
